class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.debounceTimer = null;
    this.abortController = null;

    this.onInputBound = this.onInput.bind(this);
    this.onFocusBound = this.onFocus.bind(this);
    this.onClearBound = this.onClear.bind(this);
    this.onCloseBound = this.close.bind(this);
    this.onDocumentClickBound = this.onDocumentClick.bind(this);
    this.onKeyDownBound = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('.predictive-search-input');
    this.resultsContainer = this.querySelector('.predictive-search-results-list');
    this.suggestionsContainer = this.querySelector('.js-suggestions-list');
    this.itemsCount = this.querySelector('.js-items-count');
    this.viewAllQuery = this.querySelector('.js-view-all-query');
    this.clearBtn = this.querySelector('.js-clear-btn');
    this.closeBtn = this.querySelector('.js-close-button');
    this.enableSuggestions = this.hasAttribute('data-enable-suggestions');

    this.input?.addEventListener('input', this.onInputBound);
    this.input?.addEventListener('focus', this.onFocusBound);
    this.input?.addEventListener('keydown', this.onKeyDownBound);
    this.clearBtn?.addEventListener('click', this.onClearBound);
    this.closeBtn?.addEventListener('click', this.onCloseBound);
    document.addEventListener('click', this.onDocumentClickBound);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInputBound);
    this.input?.removeEventListener('focus', this.onFocusBound);
    this.input?.removeEventListener('keydown', this.onKeyDownBound);
    this.clearBtn?.removeEventListener('click', this.onClearBound);
    this.closeBtn?.removeEventListener('click', this.onCloseBound);
    document.removeEventListener('click', this.onDocumentClickBound);
    this.abortController?.abort();
  }

  onDocumentClick(e) {
    if (!this.contains(e.target)) {
      this.close();
    }
  }

  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.input?.focus();
    }
  }

  onInput(e) {
    const query = e.target.value.trim();

    if (query.length > 0) {
      this.classList.add('is-typing');
    } else {
      this.classList.remove('is-typing');
    }

    clearTimeout(this.debounceTimer);

    if (query.length < 1) {
      this.close();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.search(query);
    }, 300);
  }

  onFocus() {
    const query = this.input.value.trim();
    const hasProducts = this.resultsContainer?.children.length > 0;
    const hasSuggestions = this.suggestionsContainer?.children.length > 0;
    if (query.length >= 1 && (hasProducts || hasSuggestions)) {
      this.open();
    }
  }

  onClear() {
    this.input.value = '';
    this.classList.remove('is-typing');
    this.close();
    this.input.focus();
  }

  async search(query) {
    const searchUrl = window?.theme?.routes?.predictiveSearchUrl;
    if (!searchUrl) return;

    this.abortController?.abort();
    this.abortController = new AbortController();

    this.classList.add('is-open', 'is-loading');
    this.classList.remove('is-results', 'is-empty', 'is-suggestions-show', 'is-products-show');

    try {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
        'resources[limit]': '10',
        section_id: 'predictive-search'
      });
      
      const requests = [
        fetch(
          searchUrl + '?' + params.toString(),
          { signal: this.abortController.signal }
        ),
      ];

      if (this.enableSuggestions) {
        const rootUrl = window.Shopify?.routes?.root || '/';
        requests.push(
          fetch(
            `${rootUrl}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=query&resources[limit]=5`,
            { signal: this.abortController.signal }
          )
        );
      }

      const responses = await Promise.all(requests);

      if (!responses[0].ok) throw new Error('Search failed');

      const text = await responses[0].text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const products = html.querySelectorAll('.predictive-search__item');

      let suggestions = [];
      if (this.enableSuggestions && responses[1]?.ok) {
        const suggestData = await responses[1].json();
        suggestions = suggestData.resources?.results?.queries || [];
      }

      this.renderResults(Array.from(products), suggestions, query);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
        this.close();
      }
    }
  }

  renderResults(products, suggestions, query) {
    this.classList.remove('is-loading');
    this.classList.add('is-results');

    const hasProducts = products.length > 0;
    const hasSuggestions = suggestions.length > 0;

    if (!hasProducts && !hasSuggestions) {
      this.classList.add('is-empty');
      this.dataset.count = '0';
      this.dataset.hasResults = 'false';
      this.resultsContainer.innerHTML = '';
      if (this.suggestionsContainer) {
        this.suggestionsContainer.innerHTML = '';
      }
      if (this.itemsCount) {
        this.itemsCount.textContent = '0';
      }
      return;
    }

    this.classList.remove('is-empty');
    this.dataset.hasResults = 'true';

    if (hasSuggestions) {
      this.classList.add('is-suggestions-show');
      this.renderSuggestions(suggestions);
    } else if (this.suggestionsContainer) {
      this.suggestionsContainer.innerHTML = '';
    }

    if (hasProducts) {
      this.classList.add('is-products-show');
      this.dataset.count = products.length.toString();

      if (this.itemsCount) {
        this.itemsCount.textContent = products.length;
      }

      this.resultsContainer.innerHTML = '';
      products.forEach((product) => {
        this.resultsContainer.appendChild(this.createProductItem(product));
      });
    } else {
      this.dataset.count = '0';
      this.resultsContainer.innerHTML = '';
      if (this.itemsCount) {
        this.itemsCount.textContent = '0';
      }
    }

    if (this.viewAllQuery) {
      this.viewAllQuery.textContent = query;
    }
  }

  renderSuggestions(suggestions) {
    if (!this.suggestionsContainer) return;

    this.suggestionsContainer.innerHTML = '';
    suggestions.forEach((suggestion) => {
      const text = suggestion.text || suggestion.styled_text || '';
      const href = suggestion.url || `/search?q=${encodeURIComponent(text)}`;

      const li = document.createElement('li');
      li.className = 'hover:bg-light-neutral-20 active:bg-dark-neutral-20';

      const a = document.createElement('a');
      a.href = href;
      a.className = 'block px-4 py-3 lg:py-2 hover:ml-1 active:ml-1 transition-all duration-200 ease-out';
      a.textContent = text;

      li.appendChild(a);
      this.suggestionsContainer.appendChild(li);
    });
  }

  createProductItem(productElement) {
    const link = productElement.querySelector('a');
    const image = productElement.querySelector('img');
    const title = productElement.querySelector('.predictive-search__item-heading')?.textContent || '';
    const vendorEl = productElement.querySelector('.item-vendor');
    const skuEl = productElement.querySelector('.item-sku');
    const href = link?.getAttribute('href') || '#';
    const imgSrc = image?.getAttribute('src') || '';
    const imgAlt = image?.getAttribute('alt') || title;

    const li = document.createElement('li');
    li.className = 'hover:bg-light-neutral-20 active:bg-dark-neutral-20';

    const a = document.createElement('a');
    a.href = href;
    a.className = 'flex gap-3 p-3 items-center';

    const imgContainer = document.createElement('div');
    imgContainer.className = 'w-16 h-16 flex-shrink-0 bg-alternate rounded overflow-hidden';

    if (imgSrc) {
      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = imgAlt;
      img.className = 'w-full h-full object-contain';
      img.loading = 'lazy';
      imgContainer.appendChild(img);
    }

    const textContainer = document.createElement('div');
    textContainer.className = 'flex-1 min-w-0';

    const titleEl = document.createElement('p');
    titleEl.className = 'font-bold line-clamp-2';
    titleEl.textContent = title;

    const vendorContainer = document.createElement('div');
    vendorContainer.className = 'text-body-3 mt-1';
    if (vendorEl) {
      vendorContainer.appendChild(vendorEl.cloneNode(true));
    }

    const skuContainer = document.createElement('div');
    skuContainer.className = 'text-body-3 mt-1';
    if (skuEl) {
      skuContainer.appendChild(skuEl.cloneNode(true));
    }

    textContainer.appendChild(titleEl);
    textContainer.appendChild(vendorContainer);
    textContainer.appendChild(skuContainer);

    a.appendChild(imgContainer);
    a.appendChild(textContainer);
    li.appendChild(a);

    return li;
  }

  open() {
    this.classList.add('is-open');
    this.input?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open', 'is-loading', 'is-results', 'is-empty', 'is-suggestions-show', 'is-products-show');
    this.input?.setAttribute('aria-expanded', 'false');
    this.abortController?.abort();
  }
}

customElements.define('predictive-search', PredictiveSearch);
